/**
 * منطقِ خالصِ «گزارش» — دلیل‌ها، برچسبشان، و پاکسازیِ توضیحِ کاربر.
 *
 * ═══ چرا این‌جا و نه داخلِ هر کدام از سه رابط ═══
 *
 * همان دلیلِ `feedback.ts` و `sourceTest.ts`: فهرستِ دلیل‌ها و ترتیبشان یک
 * تصمیمِ محصولی است که در **سه** جا دیده می‌شود (شیتِ موبایل، منوی وب،
 * صفِ ادمین) و در دیتابیس هم یک قید دارد. اگر هر رابط فهرستِ خودش را
 * بنویسد، اولین دلیلی که اضافه شود در یکی از سه‌تا جا می‌افتد و کسی
 * نمی‌فهمد — تا وقتی که کاربری در وب چیزی بفرستد که موبایل نامش را
 * نمی‌شناسد.
 *
 * پس: فهرست این‌جاست، ترجمه‌اش با کلیدِ i18n (کاربر می‌بیندش، پس ترجمه
 * می‌خواهد)، و برچسبِ فارسیِ ادمین هم این‌جا (پنلِ ادمین ترجمه ندارد).
 *
 * ⚠ هر تغییری در `REPORT_REASONS` باید با قیدِ `reason` در مایگریشن 0135
 * یکی بماند — `contentReport.test.ts` همین را می‌سنجد.
 */

/** همان مقادیری که ستونِ `content_reports.reason` می‌پذیرد. */
export type ReportReason =
  | 'spam' | 'harassment' | 'hate' | 'sexual' | 'violence' | 'false_info' | 'other';

/**
 * ترتیبِ نمایش، از رایج‌ترین به کمیاب‌ترین.
 *
 * «چیزِ دیگری» عمداً آخر است و عمداً هست: بدونش کسی که دلیلش در فهرست نیست
 * یا نزدیک‌ترین گزینه‌ی غلط را می‌زند (که آمار را خراب می‌کند) یا اصلاً
 * گزارش نمی‌دهد.
 */
export const REPORT_REASONS: ReportReason[] = [
  'spam', 'harassment', 'hate', 'sexual', 'violence', 'false_info', 'other',
];

/** کلیدِ i18nِ هر دلیل — چیزی که کاربر روی دکمه می‌بیند. */
export const REPORT_REASON_KEY: Record<ReportReason, string> = {
  spam: 'reportReasonSpam',
  harassment: 'reportReasonHarassment',
  hate: 'reportReasonHate',
  sexual: 'reportReasonSexual',
  violence: 'reportReasonViolence',
  false_info: 'reportReasonFalse',
  other: 'reportReasonOther',
};

/** برچسبِ فارسیِ صفِ ادمین. مخاطبش یک نفر است، پس از i18n رد نمی‌شود. */
export const REPORT_REASON_LABEL: Record<ReportReason, string> = {
  spam: 'هرزنامه یا کلاهبرداری',
  harassment: 'آزار و اذیت',
  hate: 'نفرت‌پراکنی',
  sexual: 'محتوای جنسی',
  violence: 'خشونت یا تهدید',
  false_info: 'اطلاعاتِ نادرست',
  other: 'چیزِ دیگری',
};

/** سه چیزی که می‌شود گزارش داد — قرینه‌ی `content_reports.target_kind`. */
export type ReportTargetKind = 'signal' | 'message' | 'profile';

/**
 * هدفِ گزارش. شناسه‌ی پیام عدد است و آن دوتای دیگر uuid — همان تفکیکی که
 * در دیتابیس هم سه ستونِ جدا شده (مایگریشن 0135).
 */
export type ReportTarget =
  | { kind: 'signal'; id: string }
  | { kind: 'message'; id: number }
  | { kind: 'profile'; id: string };

/** برچسبِ فارسیِ هدف، برای صفِ ادمین. */
export const REPORT_TARGET_LABEL: Record<ReportTargetKind, string> = {
  signal: 'سیگنال',
  message: 'پیام',
  profile: 'پروفایل',
};

export const REPORT_TARGET_ICON: Record<ReportTargetKind, string> = {
  signal: '📣',
  message: '💬',
  profile: '👤',
};

/** وضعیتِ رسیدگی — قرینه‌ی `content_reports.status`. */
export type ReportStatus = 'open' | 'actioned' | 'dismissed';

export const REPORT_STATUS_LABEL: Record<ReportStatus, string> = {
  open: 'باز',
  actioned: 'رسیدگی شد',
  dismissed: 'چیزی نبود',
};

/**
 * سقفِ توضیحِ اختیاری — همان ۱۰۰۰ی قیدِ دیتابیس.
 *
 * این‌جا هم هست تا فرم بتواند **قبل** از رفت‌وبرگشت جلویش را بگیرد: خطای
 * ۲۳۵۱۴ی پستگرس برای کاربر یعنی «یک چیزی خراب شد»، نه «زیادی نوشتی».
 */
export const REPORT_NOTE_MAX = 1000;

/**
 * توضیحِ کاربر، آماده‌ی رفتن به دیتابیس.
 *
 * فاصله‌های دو سرش می‌روند و متنِ خالی `null` می‌شود — نه رشته‌ی تهی: در صفِ
 * ادمین «توضیح: (خالی)» و «بدونِ توضیح» یک چیزند و دو شکل داشتنشان فقط یعنی
 * رابط باید هر دو را جدا بررسی کند.
 */
export function normalizeReportNote(note: string | null | undefined): string | null {
  const trimmed = (note ?? '').trim();
  if (!trimmed) return null;
  return trimmed.slice(0, REPORT_NOTE_MAX);
}

/** آیا این رشته یکی از دلیل‌های شناخته‌شده است (ورودیِ آمده از دیتابیس). */
export const isReportReason = (s: string): s is ReportReason =>
  (REPORT_REASONS as string[]).includes(s);

/**
 * ستونِ هدف، از روی نوعش.
 *
 * تنها جایی که نگاشتِ «نوع → نامِ ستون» نوشته می‌شود. `reportRepo` از همین
 * می‌سازد و صفِ ادمین از همین می‌خواند، پس اضافه شدنِ نوعِ چهارم یک جا عوض
 * می‌شود و نه سه جا.
 */
export const REPORT_TARGET_COLUMN: Record<ReportTargetKind, string> = {
  signal: 'signal_id',
  message: 'message_id',
  profile: 'profile_id',
};
